import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { Accordion } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import { useAmplitude } from '@risksmart-app/components/src/segment';
import Table from '@risksmart-app/components/src/table';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteModal from 'src/components/delete-modal';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';
import { useWizardStore } from 'src/components/wizard/store/useWizardStore';
import { useGetRiskAssessmentResultsByRiskId } from 'src/hooks/queries';
import { mapControlTypeToRatingType } from 'src/ratings/controlTypeMapper';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteAssessmentResults } from '@/hooks/mutations/assessment-result';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { getCounter } from '@/utils/collectionUtils';

import AssessmentResultModal from '../../../../assessments/modals/AssessmentResultModal';
import { useGetCollectionTableProps } from './riskRatingConfig';
import type { RiskAssessmentResultRegisterFields } from './types';

interface Props {
  risk: ObjectWithContributors & { Tier?: number };
}

const RiskRatingRegister: FC<Props> = ({ risk }) => {
  useI18NSummaryHelpContent('assessments.tabHelp');
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'ratings' });
  const amplitude = useAmplitude();

  const { data, loading, refetch } = useGetRiskAssessmentResultsByRiskId({
    queryArgs: { riskId: risk.Id },
  });

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const { currentStep, steps } = useWizardStore();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const hasLoggedViewRef = useRef(false);
  useEffect(() => {
    if (hasLoggedViewRef.current) {
      return;
    }
    hasLoggedViewRef.current = true;
    amplitude.logEvent('Risk Rating Register Viewed', {
      risk_id: risk.Id,
      ...(risk.Tier !== undefined && { risk_tier: risk.Tier }),
    });
  }, [risk.Id, risk.Tier, amplitude]);

  useEffect(() => {
    if (steps[currentStep]?.showModal === 'true') {
      setIsEditOpen(true);
    }
  }, [steps, currentStep]);

  const [selectedAssessmentResults, setSelectedAssessmentResults] = useState<
    RiskAssessmentResultRegisterFields[]
  >([]);

  const [openAssessmentResultId, setOpenAssessmentResultId] = useState<
    string | undefined
  >();

  const {
    hasPermission: userCanDeleteAssessmentResults,
    loading: userCanDeleteAssessmentResultsLoading,
  } = useHasPermissionQuery('delete:risk_assessment_result', risk);

  const { hasPermission: userCanInsertAssessmentResults } =
    useHasPermissionQuery('insert:risk_assessment_result', risk);

  // COI-522: the RCSA wizard opens this modal by setting `isEditOpen` directly
  // (see the `showModal` effect above), bypassing the `<Permission>` wrapper on
  // both "Add rating" buttons. Gate at the render boundary so every entry point
  // is covered — a user without insert was being shown a create form whose save
  // the data layer then rejected with "Access denied". Opening an existing
  // rating stays allowed: that modal is readonly, and anyone who can see the
  // ratings table can already read those rows.
  const canOpenAssessmentResultModal =
    userCanInsertAssessmentResults || !!openAssessmentResultId;

  const { deleteAssessmentResults, deleteResult } =
    useDeleteAssessmentResults();

  const handleAssessmentResultModalClose = async (saved?: boolean) => {
    if (saved) {
      await refetch();
    } else {
      const openResult = openAssessmentResultId
        ? data?.risk_assessment_result?.find(
            (r) => r.Id === openAssessmentResultId
          )
        : undefined;
      const ratingType = mapControlTypeToRatingType(openResult?.ControlType);
      amplitude.logEvent('Risk Rating Discarded', {
        mode: openAssessmentResultId ? 'update' : 'new',
        risk_id: risk.Id,
        ...(risk.Tier !== undefined && { risk_tier: risk.Tier }),
        ...(openAssessmentResultId && {
          rating_id: openAssessmentResultId,
        }),
        ...(ratingType && { rating_type: ratingType }),
      });
    }
    setOpenAssessmentResultId(undefined);
    setIsEditOpen(false);
  };

  const onAddRating = () => {
    amplitude.logEvent('Risk Rating Opened', {
      mode: 'new',
      risk_id: risk.Id,
      ...(risk.Tier !== undefined && { risk_tier: risk.Tier }),
    });
    setIsEditOpen(true);
  };

  const onOpenResult = (resultId: string) => {
    const result = data?.risk_assessment_result?.find((r) => r.Id === resultId);
    const ratingType = mapControlTypeToRatingType(result?.ControlType);

    amplitude.logEvent('Risk Rating Opened', {
      mode: 'update',
      risk_id: risk.Id,
      ...(risk.Tier !== undefined && { risk_tier: risk.Tier }),
      rating_id: resultId,
      ...(ratingType && { rating_type: ratingType }),
    });
    setOpenAssessmentResultId(resultId);
    setIsEditOpen(true);
  };
  const onDelete = useDeleteResultNotification({
    entityName: t('assessmentResults.entity_name'),
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
    asyncAction: async () => {
      const ratingIds = selectedAssessmentResults.map((r) => r.Id);

      await deleteAssessmentResults({
        items: selectedAssessmentResults.map((assessmentResult) => ({
          Id: assessmentResult.Id,
          typename: 'risk_assessment_result' as const,
        })),
      });

      amplitude.logEvent('Risk Rating Deleted', {
        risk_id: risk.Id,
        ...(risk.Tier !== undefined && { risk_tier: risk.Tier }),
        rating_ids: ratingIds,
      });

      setSelectedAssessmentResults([]);
      setIsDeleteModalVisible(false);
      refetch();

      return true;
    },
  });

  const tableProps = useGetCollectionTableProps(
    risk,
    onOpenResult,
    onAddRating,
    data?.risk_assessment_result
  );

  return (
    <>
      <SpaceBetween size={'m'}>
        <TabHeader
          actions={
            <SpaceBetween direction={'horizontal'} size={'xs'}>
              <Permission
                permission={'delete:risk_assessment_result'}
                parentObject={risk}
              >
                <Button
                  formAction={'none'}
                  variant={'normal'}
                  disabled={
                    !selectedAssessmentResults.length ||
                    !userCanDeleteAssessmentResults ||
                    userCanDeleteAssessmentResultsLoading
                  }
                  onClick={() => setIsDeleteModalVisible(true)}
                >
                  {t('assessmentResults.delete_button')}
                </Button>
              </Permission>
              <Permission
                permission={'insert:risk_assessment_result'}
                parentObject={risk}
              >
                <Button
                  iconName={'add-plus'}
                  variant={'primary'}
                  formAction={'none'}
                  onClick={onAddRating}
                >
                  {t('assessments.add_rating_button')}
                </Button>
              </Permission>
            </SpaceBetween>
          }
        >
          {st('tab_title')}
        </TabHeader>
        <Accordion defaultValue={['risk-ratings']}>
          <Accordion.Item value={'risk-ratings'}>
            <Accordion.Header>
              <Accordion.Trigger>
                <div className={'flex space-x-2'}>
                  <span>{st('riskRatingSubheading')}</span>
                  <span className={'text-grey font-normal'}>
                    {getCounter(tableProps.totalItemsCount ?? 0, loading)}
                  </span>
                </div>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content>
              <Table
                {...tableProps}
                selectionType={'multi'}
                selectedItems={selectedAssessmentResults}
                trackBy={'Id'}
                onSelectionChange={({ detail }) => {
                  setSelectedAssessmentResults(detail.selectedItems);
                }}
                resizableColumns={true}
                variant={'embedded'}
                loading={loading}
                data-testid={'risk-rating-table'}
              />
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
      </SpaceBetween>
      {isEditOpen && canOpenAssessmentResultModal && (
        <AssessmentResultModal
          id={openAssessmentResultId}
          onDismiss={handleAssessmentResultModalClose}
          assessedItem={risk}
          resultType={Parent_Type_Enum.RiskAssessmentResult}
          hideTypeSelector={true}
          i18n={t('ratings')}
          assessmentMode={'rating'}
        />
      )}
      <DeleteModal
        loading={deleteResult.loading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => {
          setOpenAssessmentResultId(undefined);
          setSelectedAssessmentResults([]);
          setIsDeleteModalVisible(false);
        }}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};
export default RiskRatingRegister;
